var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { CONFIG } from "@/config";
import { vertexShader } from "@/shaders/vertexShader";
import { useFrame } from "@react-three/fiber";
import { useAtomValue } from "jotai";
import { clamp } from "lodash";
import React, { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { AnimationMixer, BackSide, Bone, FrontSide, LoopOnce, SkinnedMesh, } from "three";
import { PageFXPlayerAtom } from "../audio/PageFXPlayer";
import { CancellationToken } from "../loader/PriorityLoader";
import { useMultiResTextureLoader } from "./pageTextureLoader";
import { useGLTFWithCORS, useTextureWithCORS, } from "../utils/three-loader-hooks";
import { getObjectByName, getOptionalObjectByName } from "@/utils/three";
import { usePageInkShader } from "@/shaders/inkShader";
var SkeletonUtils = require("three/examples/jsm/utils/SkeletonUtils");
export var PageSide;
(function (PageSide) {
    PageSide["Left"] = "left";
    PageSide["Right"] = "right";
})(PageSide || (PageSide = {}));
export function Page(_a) {
    var _b;
    var animationStartTime = _a.animationStartTime, animating = _a.animating, revealedSides = _a.revealedSides, onTransitionEnd = _a.onTransitionEnd, frontTexture = _a.frontTexture, backTexture = _a.backTexture, side = _a.side, depth = _a.depth, fold = _a.fold, rest = __rest(_a, ["animationStartTime", "animating", "revealedSides", "onTransitionEnd", "frontTexture", "backTexture", "side", "depth", "fold"]);
    var pageSfxPlayer = useAtomValue(PageFXPlayerAtom);
    useEffect(function () {
        if (animating) {
            var delay = animationStartTime - Date.now();
            var timeout_1 = setTimeout(function () {
                pageSfxPlayer.play();
            }, delay);
            return function () {
                clearTimeout(timeout_1);
            };
        }
    }, [animating, animationStartTime, pageSfxPlayer]);
    var gltf = useGLTFWithCORS(CONFIG.models.page.file);
    var scene = useMemo(function () {
        return SkeletonUtils.clone(gltf.scene);
    }, [gltf]);
    // FIXME: should use getObjectByName but sometimes returns null
    var bone = getOptionalObjectByName(scene, CONFIG.models.page.bone, Bone);
    var plane = getObjectByName(scene, CONFIG.models.page.plane, SkinnedMesh);
    var _c = useMemo(function () {
        var mixer = new AnimationMixer(plane);
        var toLeftAnimation = gltf.animations[0].clone();
        var toRightAnimation = gltf.animations[0].clone();
        var foldAnimation = gltf.animations[0].clone();
        var animationsDurations = CONFIG.models.page.animationsDurations;
        // Animations in GLFT are in order: left, right, fold
        toLeftAnimation.tracks = toLeftAnimation.tracks.map(function (t) {
            return t.trim(0, animationsDurations.toLeft);
        });
        toRightAnimation.tracks.map(function (t) {
            return t.shift(-animationsDurations.toLeft).trim(0, animationsDurations.toRight);
        });
        foldAnimation.tracks.map(function (t) {
            return t
                .shift(-animationsDurations.toLeft - animationsDurations.toRight)
                .trim(0, animationsDurations.fold);
        });
        toLeftAnimation.resetDuration();
        toRightAnimation.resetDuration();
        foldAnimation.resetDuration();
        var toLeftAction = mixer.clipAction(toLeftAnimation);
        var toRightAction = mixer.clipAction(toRightAnimation);
        var foldAction = mixer.clipAction(foldAnimation);
        toLeftAction.clampWhenFinished = true;
        toRightAction.clampWhenFinished = true;
        toLeftAction.loop = LoopOnce;
        toRightAction.loop = LoopOnce;
        foldAction.loop = LoopOnce;
        foldAction.zeroSlopeAtEnd = true;
        toLeftAction.stop();
        toRightAction.stop();
        foldAction.stop();
        toLeftAction.weight = 0;
        toRightAction.weight = 0;
        // mixer.addEventListener('finished', (e) => {
        //   if (e)
        // })
        return { mixer: mixer, toLeftAction: toLeftAction, toRightAction: toRightAction, foldAction: foldAction };
    }, [plane, gltf.animations]), mixer = _c.mixer, toLeftAction = _c.toLeftAction, toRightAction = _c.toRightAction, foldAction = _c.foldAction;
    var lastTime = useRef({ now: null });
    var from = side === PageSide.Right ? toLeftAction : toRightAction;
    var to = side === PageSide.Right ? toRightAction : toLeftAction;
    var WEIGHT_CROSSFADE_DURATION = 0.3;
    var endedState = useRef({
        endedSince: null,
    });
    var pageLeft = useTextureWithCORS(CONFIG.textures.emptyPage.left.src, {
        flipY: false,
    });
    var pageRight = useTextureWithCORS(CONFIG.textures.emptyPage.right.src, {
        flipY: false,
    });
    var pageLeftAmbiance = useTextureWithCORS(CONFIG.textures.pageAmbianceLayer.left.src, { flipY: false });
    var pageRightAmbiance = useTextureWithCORS(CONFIG.textures.pageAmbianceLayer.right.src, { flipY: false });
    // Build shaders
    var _d = CONFIG.fadeInShader, fadeInDuration = _d.duration, fadeInDelay = _d.delay;
    var usePageShader = usePageInkShader;
    var frontShader = usePageShader(pageRight, pageRightAmbiance, frontTexture !== null && frontTexture !== void 0 ? frontTexture : undefined);
    var backShader = usePageShader(pageLeft, pageLeftAmbiance, backTexture !== null && backTexture !== void 0 ? backTexture : undefined);
    // Initialize shaders with common properties
    useEffect(function () {
        frontShader.side = FrontSide;
        frontShader.uniforms.flipX = { value: 0 };
        backShader.side = BackSide;
        backShader.uniforms.flipX = { value: 1 };
        [frontShader, backShader].forEach(function (shader) {
            shader.vertexShader = vertexShader;
            shader.uniforms.p = { value: 0 };
            shader.needsUpdate = true;
            shader.uniformsNeedUpdate = true;
        });
    }, [frontShader, backShader]);
    useLayoutEffect(function () {
        endedState.current.endedSince = null;
    }, [animating, side]);
    // Progress of the fade in shader
    var progress = useRef((_b = {},
        _b[PageSide.Left] = {
            elapsed: 0,
            // animating && revealedSides.indexOf(PageSide.Left) === -1 ? 0 : 9999,
        },
        _b[PageSide.Right] = {
            elapsed: 0,
            // animating && revealedSides.indexOf(PageSide.Right) === -1 ? 0 : 9999,
        },
        _b));
    useFrame(function (_a) {
        var clock = _a.clock;
        var now = clock.getElapsedTime();
        if (lastTime.current.now != null) {
            if (groupRef.current != null) {
                groupRef.current.visible = true;
            }
            var dt = now - lastTime.current.now;
            mixer.update(dt);
            to.weight = Math.min(1, to.weight + dt / WEIGHT_CROSSFADE_DURATION);
            from.weight = 1 - to.weight;
            // Update progresses
            if (revealedSides.indexOf(PageSide.Right) !== -1) {
                var p = progress.current[PageSide.Right];
                p.elapsed += dt;
            }
            else {
                var p = progress.current[PageSide.Right];
                p.elapsed = 0;
            }
            if (revealedSides.indexOf(PageSide.Left) !== -1) {
                var p = progress.current[PageSide.Left];
                p.elapsed += dt;
            }
            else {
                var p = progress.current[PageSide.Left];
                p.elapsed = 0;
            }
        }
        // Update shaders
        frontShader.uniforms.p = {
            value: clamp((progress.current[PageSide.Right].elapsed - fadeInDelay) /
                fadeInDuration, 0, 1),
        };
        backShader.uniforms.p = {
            value: clamp((progress.current[PageSide.Left].elapsed - fadeInDelay) /
                fadeInDuration, 0, 1),
        };
        frontShader.uniformsNeedUpdate = true;
        backShader.uniformsNeedUpdate = true;
        lastTime.current.now = clock.getElapsedTime();
        var ended = animating && to.time >= to.getClip().duration;
        if (ended && endedState.current.endedSince == null) {
            endedState.current.endedSince = now;
        }
        if (!ended) {
            endedState.current.endedSince = null;
        }
        if (endedState.current.endedSince != null &&
            now - endedState.current.endedSince > 0.1) {
            endedState.current.endedSince = null;
            onTransitionEnd === null || onTransitionEnd === void 0 ? void 0 : onTransitionEnd();
        }
        animationProgress.current = to.time / to.getClip().duration;
        if (groupRef.current != null) {
            groupRef.current.position.y = getDepth();
        }
    });
    var initAnim = useRef({ initialized: false });
    useLayoutEffect(function () {
        if (!animating)
            return;
        if (!initAnim.current.initialized) {
            initAnim.current.initialized = true;
            to.weight = 1;
            from.weight = 0;
            from.time = from.getClip().duration;
        }
        var fromProgress = from.time / from.getClip().duration;
        var dt = (1 - fromProgress) * to.getClip().duration;
        from.paused = true;
        to.stop();
        to.time = dt;
        var delay = animationStartTime - Date.now();
        if (delay < 0) {
            to.paused = false;
            to.timeScale = 1.3;
            to.play();
        }
        else {
            to.paused = false;
            to.play();
            to.timeScale = 0;
            setTimeout(function () {
                to.timeScale = 1.3;
            }, delay);
        }
    }, [from, to, animating, animationStartTime]);
    useLayoutEffect(function () {
        if (!animating) {
            to.play();
            to.time = to.getClip().duration;
            to.weight = 1;
            from.paused = true;
            from.weight = 0;
            foldAction.weight = 0;
        }
    }, [animating, to, from, foldAction]);
    var groupRef = useRef();
    var animationProgress = useRef(0);
    function getDepthAdjust() {
        return 1 - Math.abs(animationProgress.current - 0.5) * 2;
    }
    function getDepth() {
        return (depth - getDepthAdjust() * 10) * -0.005;
    }
    // Play fold animation when prop is set to true
    useLayoutEffect(function () {
        if (fold && !foldAction.isRunning()) {
            foldAction.weight = 1;
            foldAction.reset().play();
        }
    }, [fold, foldAction]);
    var _e = CONFIG.pages, modelRes = _e.modelRes, targetRes = _e.targetRes, baseScale = _e.scale, offset = _e.offset;
    var baseRatio = modelRes.width / modelRes.height;
    var targetRatio = targetRes.width / targetRes.height;
    return (React.createElement("group", __assign({}, rest, { position: [offset.x, getDepth(), offset.y], ref: groupRef, visible: false, scale: [baseScale * (targetRatio / baseRatio), baseScale, baseScale] }),
        React.createElement("group", { rotation: [Math.PI / 2, 0, 0] },
            React.createElement("primitive", { object: bone }),
            React.createElement("skinnedMesh", { 
                // needed when zooming:
                // large difference between original mesh & skinned version
                frustumCulled: false, geometry: plane.geometry, skeleton: plane.skeleton, material: frontShader }),
            React.createElement("skinnedMesh", { 
                // needed when zooming:
                // large difference between original mesh & skinned version
                frustumCulled: false, geometry: plane.geometry, skeleton: plane.skeleton, material: backShader }))));
}
var DEFAULT_PAGE_CONTENT_LEFT = CONFIG.textures.emptyPage.left.src;
var DEFAULT_PAGE_CONTENT_RIGHT = CONFIG.textures.emptyPage.right.src;
export function PageLoader(_a) {
    var inFront = _a.inFront, pageFront = _a.pageFront, pageBack = _a.pageBack, pages = _a.pages, rest = __rest(_a, ["inFront", "pageFront", "pageBack", "pages"]);
    var cancellationToken = useMemo(function () { return new CancellationToken(); }, []);
    useEffect(function () {
        return function () {
            cancellationToken.cancel();
        };
    }, [cancellationToken]);
    var frontPageTextures = useMemo(function () {
        var textures = pages.get(pageFront);
        return __spreadArray([DEFAULT_PAGE_CONTENT_RIGHT], __read(textures), false);
    }, [pages, pageFront]);
    var backPageTextures = useMemo(function () {
        var textures = pages.get(pageBack);
        return __spreadArray([DEFAULT_PAGE_CONTENT_LEFT], __read(textures), false);
    }, [pages, pageBack]);
    var hdFront = inFront && rest.side === PageSide.Right;
    var hdBack = inFront && rest.side === PageSide.Left;
    var frontTexture = useMultiResTextureLoader(cancellationToken, frontPageTextures, hdFront ? frontPageTextures.length : 2);
    var backTexture = useMultiResTextureLoader(cancellationToken, backPageTextures, hdBack ? backPageTextures.length : 2);
    return (React.createElement(Page, __assign({ frontTexture: frontTexture, backTexture: backTexture }, rest)));
}
